"use client";

import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '@radix-ui/themes';
import { splitSocialPostEditFields } from '@/lib/social-media/social-post-edit-fields';
import { Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { SocialPostRow } from './SocialMediaViewModal';

export default function SocialMediaEditModal({
  post,
  open,
  onOpenChange,
  onSaved,
}: {
  post: SocialPostRow | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSaved: () => void;
}) {
  const [message, setMessage] = useState('');
  const [linksText, setLinksText] = useState('');
  const [tagsText, setTagsText] = useState('');
  const [initialLinksText, setInitialLinksText] = useState('');
  const [initialTagsText, setInitialTagsText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (post && open) {
      const fields = splitSocialPostEditFields({
        message: post.message || '',
        links: post.links || [],
        tags: post.tags || [],
      });
      const nextLinksText = fields.links.join('\n');
      const nextTagsText = fields.tags.join(', ');

      setMessage(fields.message);
      setLinksText(nextLinksText);
      setTagsText(nextTagsText);
      setInitialLinksText(nextLinksText);
      setInitialTagsText(nextTagsText);
      setError(null);
    }
  }, [post, open]);

  if (!post) return null;

  const isText = post.postType === 'text';

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const links = linksText
        .split(/\n+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const tags = tagsText
        .split(/[,]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const payload: { message: string; links?: string[]; tags?: string[] } = { message };
      if (linksText !== initialLinksText) payload.links = links;
      if (tagsText !== initialTagsText) payload.tags = tags;

      const res = await fetch(`/api/admin/social-media/posts/${post._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      onSaved();
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(92vw,480px)] max-h-[90vh] overflow-y-auto -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white p-6 shadow-xl border border-gray-200 focus:outline-none">
          <div className="flex items-start justify-between gap-3 mb-4">
            <Dialog.Title className="text-lg font-semibold text-gray-900">Edit post</Dialog.Title>
            <Dialog.Close className="rounded-md p-1 text-gray-500 hover:bg-gray-100">
              <X className="h-5 w-5" />
            </Dialog.Close>
          </div>

          <p className="text-xs text-gray-500 mb-4">
            {isText && 'Text posts: full message, links, and tags.'}
            {post.postType === 'photo' && 'Photo posts: caption, links, and tags only (image cannot be changed).'}
            {post.postType === 'video' && 'Video posts: caption, links, and tags only (video cannot be changed).'}
          </p>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                {isText ? 'Message' : 'Caption'}
              </label>
              <textarea
                className="w-full min-h-[120px] rounded-md border border-gray-200 px-3 py-2 text-sm"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Links (one per line)</label>
              <textarea
                className="w-full min-h-[72px] rounded-md border border-gray-200 px-3 py-2 text-sm font-mono"
                value={linksText}
                onChange={(e) => setLinksText(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Tags (comma-separated)</label>
              <input
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                value={tagsText}
                onChange={(e) => setTagsText(e.target.value)}
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-2 pt-2">
              <Dialog.Close asChild>
                <Button variant="soft" color="gray" type="button">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button type="button" onClick={handleSave} disabled={saving} style={{ backgroundColor: '#007bff' }}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save changes'}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
